import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to get headers with current token
const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// Get wallet details
export const getWallet = createAsyncThunk(
    "wallet/getWallet",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/wallet`, { 
                headers: getHeaders() 
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Add credits to wallet
export const addCredits = createAsyncThunk(
    "wallet/addCredits",
    async ({ packageId, type, amount }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/wallet/credits`,
                { packageId, type, amount },
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Use credits from wallet
export const useCredits = createAsyncThunk(
    "wallet/useCredits",
    async ({ type, amount }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/wallet/credits/use`,
                { type, amount },
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Get transaction history
export const getTransactionHistory = createAsyncThunk(
    "wallet/getTransactionHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/wallet/transactions`, {
                headers: getHeaders()
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Get credit balance
export const getCreditBalance = createAsyncThunk(
    "wallet/getCreditBalance",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/wallet/balance`, {
                headers: getHeaders()
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Get credit usage history
export const getCreditUsageHistory = createAsyncThunk(
    "wallet/getCreditUsageHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/wallet/usage-history`, {
                headers: getHeaders()
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const walletSlice = createSlice({
    name: "wallet",
    initialState: {
        wallet: null,
        credits: null,
        transactions: [],
        usageHistory: [],
        loading: false,
        addCreditsLoading: false,
        useCreditsLoading: false,
        transactionsLoading: false,
        balanceLoading: false,
        usageHistoryLoading: false,
        error: null,
        addCreditsError: null,
        useCreditsError: null,
        transactionsError: null,
        balanceError: null,
        usageHistoryError: null,
    },
    reducers: {
        clearWalletError: (state) => {
            state.error = null;
        },
        clearAddCreditsError: (state) => {
            state.addCreditsError = null;
        },
        clearUseCreditsError: (state) => {
            state.useCreditsError = null;
        },
        clearTransactionsError: (state) => {
            state.transactionsError = null;
        },
        clearBalanceError: (state) => {
            state.balanceError = null;
        },
        clearUsageHistoryError: (state) => {
            state.usageHistoryError = null;
        },
        clearAllErrors: (state) => {
            state.error = null;
            state.addCreditsError = null;
            state.useCreditsError = null;
            state.transactionsError = null;
            state.balanceError = null;
            state.usageHistoryError = null;
        },
        resetWallet: (state) => {
            state.wallet = null;
            state.credits = null;
            state.transactions = [];
            state.usageHistory = [];
            state.loading = false;
            state.addCreditsLoading = false;
            state.useCreditsLoading = false;
            state.transactionsLoading = false;
            state.balanceLoading = false;
            state.usageHistoryLoading = false;
            state.error = null;
            state.addCreditsError = null;
            state.useCreditsError = null;
            state.transactionsError = null;
            state.balanceError = null;
            state.usageHistoryError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Wallet
            .addCase(getWallet.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getWallet.fulfilled, (state, action) => {
                state.loading = false;
                state.wallet = action.payload;
                state.error = null;
            })
            .addCase(getWallet.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Add Credits
            .addCase(addCredits.pending, (state) => {
                state.addCreditsLoading = true;
                state.addCreditsError = null;
            })
            .addCase(addCredits.fulfilled, (state, action) => {
                state.addCreditsLoading = false;
                state.wallet = action.payload;
                state.addCreditsError = null;
            })
            .addCase(addCredits.rejected, (state, action) => {
                state.addCreditsLoading = false;
                state.addCreditsError = action.payload;
            })

            // Use Credits
            .addCase(useCredits.pending, (state) => {
                state.useCreditsLoading = true;
                state.useCreditsError = null;
            })
            .addCase(useCredits.fulfilled, (state, action) => {
                state.useCreditsLoading = false;
                state.wallet = action.payload;
                state.useCreditsError = null;
            })
            .addCase(useCredits.rejected, (state, action) => {
                state.useCreditsLoading = false;
                state.useCreditsError = action.payload;
            })

            // Get Transaction History
            .addCase(getTransactionHistory.pending, (state) => {
                state.transactionsLoading = true;
                state.transactionsError = null;
            })
            .addCase(getTransactionHistory.fulfilled, (state, action) => {
                state.transactionsLoading = false;
                state.transactions = action.payload;
                state.transactionsError = null;
            })
            .addCase(getTransactionHistory.rejected, (state, action) => {
                state.transactionsLoading = false;
                state.transactionsError = action.payload;
            })

            // Get Credit Balance
            .addCase(getCreditBalance.pending, (state) => {
                state.balanceLoading = true;
                state.balanceError = null;
            })
            .addCase(getCreditBalance.fulfilled, (state, action) => {
                state.balanceLoading = false;
                state.credits = action.payload;
                state.balanceError = null;
            })
            .addCase(getCreditBalance.rejected, (state, action) => {
                state.balanceLoading = false;
                state.balanceError = action.payload;
            })

            // Get Credit Usage History
            .addCase(getCreditUsageHistory.pending, (state) => {
                state.usageHistoryLoading = true;
                state.usageHistoryError = null;
            })
            .addCase(getCreditUsageHistory.fulfilled, (state, action) => {
                state.usageHistoryLoading = false;
                state.usageHistory = action.payload.data || action.payload;
                state.usageHistoryError = null;
            })
            .addCase(getCreditUsageHistory.rejected, (state, action) => {
                state.usageHistoryLoading = false;
                state.usageHistoryError = action.payload;
            });
    },
});

export const {
    clearWalletError,
    clearAddCreditsError,
    clearUseCreditsError,
    clearTransactionsError,
    clearBalanceError,
    clearUsageHistoryError,
    clearAllErrors,
    resetWallet,
} = walletSlice.actions;

// Selectors
export const selectWallet = (state) => state.wallet.wallet;
export const selectCredits = (state) => state.wallet.credits;
export const selectTransactions = (state) => state.wallet.transactions;
export const selectUsageHistory = (state) => state.wallet.usageHistory;
export const selectWalletLoading = (state) => state.wallet.loading;
export const selectAddCreditsLoading = (state) => state.wallet.addCreditsLoading;
export const selectUseCreditsLoading = (state) => state.wallet.useCreditsLoading;
export const selectTransactionsLoading = (state) => state.wallet.transactionsLoading;
export const selectBalanceLoading = (state) => state.wallet.balanceLoading;
export const selectUsageHistoryLoading = (state) => state.wallet.usageHistoryLoading;
export const selectWalletError = (state) => state.wallet.error;
export const selectAddCreditsError = (state) => state.wallet.addCreditsError;
export const selectUseCreditsError = (state) => state.wallet.useCreditsError;
export const selectTransactionsError = (state) => state.wallet.transactionsError;
export const selectBalanceError = (state) => state.wallet.balanceError;
export const selectUsageHistoryError = (state) => state.wallet.usageHistoryError;

export default walletSlice.reducer;